import React, { useEffect, useRef, useState } from 'react';
import { NameResult } from '../types/NameResult';
import { LuCopy, LuCheck, LuEyeOff, LuExternalLink, LuChevronDown, LuChevronUp } from 'react-icons/lu';
import { SiX, SiInstagram, SiTiktok, SiGithub } from 'react-icons/si';

interface OverflowMenuProps {
  result: NameResult;
  activeTld: string;
  onDismiss: () => void;
  onClose: () => void;
}

// Static registrar price table (approximate, USD/yr)
const REGISTRAR_PRICES: Record<string, Record<string, string>> = {
  Namecheap: { '.com': '$9', '.io': '$35', '.dev': '$13', '.ai': '$70', '.co': '$28' },
  Cloudflare: { '.com': '$10', '.io': '$32', '.dev': '$12', '.ai': '$—', '.co': '$25' },
  'Google Domains': { '.com': '$12', '.io': '$40', '.dev': '$14', '.ai': '$—', '.co': '$30' },
};

const REGISTRAR_URLS: Record<string, (name: string, tld: string) => string> = {
  Namecheap: (n, t) => `https://www.namecheap.com/domains/registration/results/?domain=${n}${t}`,
  Cloudflare: (n, t) => `https://www.cloudflare.com/products/registrar/`,
  'Google Domains': (n, t) => `https://domains.google/`,
};

const SOCIAL_PLATFORMS = [
  { id: 'x', label: 'X / Twitter', icon: SiX, url: (n: string) => `https://x.com/${n}` },
  { id: 'instagram', label: 'Instagram', icon: SiInstagram, url: (n: string) => `https://instagram.com/${n}` },
  { id: 'tiktok', label: 'TikTok', icon: SiTiktok, url: (n: string) => `https://tiktok.com/@${n}` },
  { id: 'github', label: 'GitHub', icon: SiGithub, url: (n: string) => `https://github.com/${n}` },
];

export const OverflowMenu: React.FC<OverflowMenuProps> = ({
  result,
  activeTld,
  onDismiss,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Close on outside click / Escape
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(`${result.name}${activeTld}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const toggleSection = (id: string) =>
    setExpandedSection((prev) => (prev === id ? null : id));

  // Score breakdown from rationale
  const rationale = result.score?.rationale;
  const scores = result.score?.scores;

  return (
    <div
      ref={ref}
      className="absolute right-0 bottom-full mb-1 z-50 w-64 bg-[#111120] rounded-xl border border-[#1c1c2e] shadow-2xl overflow-hidden"
    >
      {/* Name breakdown */}
      <MenuSection
        id="breakdown"
        label="Name breakdown"
        expanded={expandedSection === 'breakdown'}
        onToggle={() => toggleSection('breakdown')}
      >
        {scores && rationale ? (
          <ul className="space-y-1.5 py-1">
            {Object.entries(scores).map(([key, val]) => {
              const label = key.replace(/_/g, ' ');
              const note = (rationale as any)[key] as string | undefined;
              const pct = Math.round((val / 5) * 100);
              return (
                <li key={key}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs text-gray-400 capitalize">{label}</span>
                    <span className="text-xs font-semibold text-gray-800">{val}/5</span>
                  </div>
                  <div className="h-1 bg-[#1c1c2e] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-indigo-400"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {note && (
                    <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{note}</p>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-xs text-gray-400 py-1">No score data available.</p>
        )}
      </MenuSection>

      {/* Social handle availability */}
      <MenuSection
        id="social"
        label="Social handles"
        expanded={expandedSection === 'social'}
        onToggle={() => toggleSection('social')}
      >
        <ul className="space-y-1 py-1">
          {SOCIAL_PLATFORMS.map(({ id, label, icon: Icon, url }) => (
            <li key={id} className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-xs text-gray-400">
                <Icon className="w-3.5 h-3.5" />
                {label}
              </span>
              <a
                href={url(result.name)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-0.5 text-[11px] text-indigo-600 hover:text-indigo-800"
              >
                @{result.name}
                <LuExternalLink className="w-2.5 h-2.5" />
              </a>
            </li>
          ))}
        </ul>
        <p className="text-[10px] text-gray-400 mt-1">Links open the profile page — check manually for availability.</p>
      </MenuSection>

      {/* TLD price comparison */}
      <MenuSection
        id="pricing"
        label="TLD price comparison"
        expanded={expandedSection === 'pricing'}
        onToggle={() => toggleSection('pricing')}
      >
        <div className="overflow-x-auto py-1">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-gray-400">
                <th className="text-left font-medium pb-1">Registrar</th>
                {[activeTld, ...['.com', '.io', '.dev'].filter((t) => t !== activeTld)].slice(0, 3).map((t) => (
                  <th key={t} className="text-right font-mono font-medium pb-1">{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(REGISTRAR_PRICES).map(([registrar, prices]) => (
                <tr key={registrar} className="border-t border-[#1a1a2e]">
                  <td className="py-1 pr-2">
                    <a
                      href={REGISTRAR_URLS[registrar](result.name, activeTld)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5"
                    >
                      {registrar}
                      <LuExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                    </a>
                  </td>
                  {[activeTld, ...['.com', '.io', '.dev'].filter((t) => t !== activeTld)].slice(0, 3).map((t) => (
                    <td key={t} className="text-right text-gray-400 py-1">
                      {prices[t] ?? '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-gray-400 mt-1.5">Prices approximate, per year.</p>
        </div>
      </MenuSection>

      {/* Divider */}
      <div className="border-t border-[#1c1c2e]" />

      {/* Copy as text */}
      <button
        onClick={handleCopy}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-300 hover:bg-[#1a1a2e] transition-colors"
      >
        {copied ? (
          <LuCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
        ) : (
          <LuCopy className="w-4 h-4 flex-shrink-0" />
        )}
        {copied ? 'Copied!' : `Copy ${result.name}${activeTld}`}
      </button>

      {/* Dismiss */}
      <button
        onClick={onDismiss}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-500 hover:bg-[#1a1a2e] hover:text-red-400 transition-colors"
      >
        <LuEyeOff className="w-4 h-4 flex-shrink-0" />
        Dismiss
      </button>
    </div>
  );
};

function MenuSection({
  id,
  label,
  expanded,
  onToggle,
  children,
}: {
  id: string;
  label: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-[#1c1c2e] last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-gray-300 hover:bg-[#1a1a2e] transition-colors"
      >
        {label}
        {expanded ? (
          <LuChevronUp className="w-3.5 h-3.5 text-gray-400" />
        ) : (
          <LuChevronDown className="w-3.5 h-3.5 text-gray-400" />
        )}
      </button>
      {expanded && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}
